import {
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  Text,
} from '@/components';
import { useTenantManagerPermission } from '@/features/authentication/_hooks/permission';
import { TABLE_ROW_MENU_WIDTH, dateFormat } from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type z from 'zod';
import { useDeleteSnapshot, useSnapshots } from '../_hooks/useSnapshot';
import {
  SnapShotStatusLabelModifier,
  type SnapshotsModel,
} from '../_model/snapshot';
import { useSnapshotDrawerState } from './upset-drawer/SnapshotDrawerStateProvider';

export const SnapshotTable: FC = () => {
  const { t } = useTranslation();
  const snapshots = useSnapshots();
  const { setDrawerState } = useSnapshotDrawerState();
  const { data: editPermission } = useTenantManagerPermission(true);
  const navigate = useNavigate();
  const { mutateAsync: deleteSnapshotAsync } = useDeleteSnapshot();

  const [deleteTarget, setDeleteTarget] = useState<z.infer<
    typeof SnapshotsModel.element
  > | null>(null);

  if (snapshots.isPending || !snapshots.data) {
    return <Loading />;
  }

  return (
    <>
      <Table
        dataSource={snapshots.data}
        rowKey="id"
        columns={[
          {
            title: t('snapshot.date'),
            dataIndex: 'referenceDate',
            width: 150,
            render: (_, record) => (
              <Text
                text={dateFormat({ datetime: record.referenceDate })}
                weight="regular"
              />
            ),
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
            render: (_, { name }) => <Text text={name} weight="regular" />,
          },
          {
            title: t('snapshot.status'),
            dataIndex: 'status',
            width: 150,
            render: (_, record) => (
              <Text
                text={SnapShotStatusLabelModifier[record.status]}
                weight="regular"
              />
            ),
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: 150,
            render: (_, { startYear }) => (
              <Text text={startYear} weight="regular" />
            ),
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: 150,
            render: (_, { endYear }) => (
              <Text text={endYear} weight="regular" />
            ),
          },
          {
            title: '',
            key: 'actions',
            fixed: 'right' as const,
            width: TABLE_ROW_MENU_WIDTH,
            onCell: () => ({
              style: { minWidth: TABLE_ROW_MENU_WIDTH },
            }),
            render: (_, record) => (
              <TableRowMenuCell
                wrapperStyle={{ justifyContent: 'flex-end' }}
                hideEditIcon={true}
                onBrowse={
                  record.status !== 'success'
                    ? undefined
                    : () => {
                        navigate(
                          `/tenant-manager/snapshots/${record.id}/users`,
                        );
                      }
                }
                onEdit={
                  editPermission
                    ? () => {
                        setDrawerState({
                          open: true,
                          isEditing: true,
                          id: record.id,
                          initialValues: {
                            name: record.name,
                            referenceDate: dayjs(record.referenceDate),
                            period: [
                              record.startYear
                                ? dayjs().year(record.startYear)
                                : undefined,
                              record.endYear
                                ? dayjs().year(record.endYear)
                                : undefined,
                            ],
                          },
                        });
                      }
                    : undefined
                }
                items={
                  editPermission
                    ? [
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () => {
                            setDeleteTarget(record);
                          },
                        },
                      ]
                    : undefined
                }
              />
            ),
          },
        ]}
      />

      <ConfirmModal
        open={deleteTarget !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: deleteTarget?.name ?? '',
        })}
        onOk={async () => {
          if (deleteTarget) {
            try {
              await deleteSnapshotAsync(deleteTarget.id);
            } finally {
              setDeleteTarget(null);
            }
          }
        }}
        onCancel={() => {
          setDeleteTarget(null);
        }}
        skipCheck
      />
    </>
  );
};
